import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Database,
  FileInput,
  PencilRuler,
  Plus,
  RefreshCw,
  Tv,
} from "lucide-react";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { datasetApi } from "../../api/dataSources";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Banner, Button, Card, EmptyState, PageHeader, SkeletonRows, Stat, formatRelative } from "../../ds";
import { reportStatus } from "./reportStatus";

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

const EVENT_LABELS: Record<string, string> = {
  login_success: "signed in",
  publication_succeeded: "published a report",
  publication_failure: "tried to publish (failed validation)",
  device_activated: "approved a TV screen",
  tv_activation_requested: "A TV asked to be activated",
  dataset_refresh: "refreshed a dataset",
  dataset_updated: "updated a dataset",
  report_created: "created a report",
  powerbi_import: "imported a Power BI report",
  bootstrap_admin_granted: "granted platform admin to a user",
};

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reports = useQuery({ queryKey: ["admin", "reports"], queryFn: adminApi.listReports });
  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices });
  const runs = useQuery({ queryKey: ["admin", "refresh-runs"], queryFn: datasetApi.listRecentRefreshRuns });
  const datasets = useQuery({ queryKey: ["admin", "datasets"], queryFn: datasetApi.list });
  const audit = useQuery({ queryKey: ["admin", "audit"], queryFn: adminApi.listAuditLogs });
  const health = useQuery({ queryKey: ["admin", "health"], queryFn: adminApi.systemHealth });
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });
  const nameOf = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u.display_name || u.email])), [users.data]);

  const active = (reports.data ?? []).filter((r) => r.status !== "deleted");
  const published = active.filter((r) => r.current_published_version_id);
  const pendingDevices = (devices.data ?? []).filter((d) => d.status === "pending");
  const activeDevices = (devices.data ?? []).filter((d) => d.status === "active");
  const failedRuns = (runs.data ?? []).filter((r) => r.status === "failed");
  const unhealthy = Object.entries(health.data?.dependencies ?? {}).filter(([, v]) => v !== "ok");

  const attention = useMemo(() => {
    const items: Array<{ key: string; tone: "warning" | "danger" | "info"; title: string; body: string; cta: string; to: string }> = [];
    if (pendingDevices.length)
      items.push({ key: "dev", tone: "info", title: `${pendingDevices.length} TV screen${pendingDevices.length > 1 ? "s are" : " is"} waiting to be approved`, body: "Each new TV shows a 6-character code. Approve it and choose which screen group it belongs to.", cta: "Review screens", to: "/admin/devices" });
    const failedPubs = active.filter((r) => r.has_failed_publication);
    if (failedPubs.length)
      items.push({ key: "pub", tone: "warning", title: `${failedPubs.length} report${failedPubs.length > 1 ? "s" : ""} failed to publish last time`, body: failedPubs.map((r) => r.name).slice(0, 3).join(", "), cta: "Open reports", to: "/admin/reports?filter=attention" });
    const notOnScreens = published.filter((r) => reportStatus(r).kind === "published");
    if (notOnScreens.length)
      items.push({ key: "screens", tone: "info", title: `${notOnScreens.length} published report${notOnScreens.length > 1 ? "s aren't" : " isn't"} on any screen yet`, body: "Choose a screen group or single screens for it under Screens & access.", cta: "Choose screens", to: "/admin/reports?filter=offscreen" });
    if (failedRuns.length)
      items.push({ key: "runs", tone: "danger", title: `${failedRuns.length} data refresh${failedRuns.length > 1 ? "es" : ""} failed`, body: failedRuns.map((r) => r.dataset_name).slice(0, 3).join(", "), cta: "Check data", to: "/admin/data" });
    if (unhealthy.length)
      items.push({ key: "health", tone: "danger", title: "A platform service needs attention", body: unhealthy.map(([k, v]) => `${k}: ${v}`).join(" · "), cta: "System health", to: "/admin/system-health" });
    return items;
  }, [pendingDevices.length, active, published, failedRuns, unhealthy]);

  const recent = [...active].sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? "")).slice(0, 6);
  const activity = (audit.data ?? []).filter((e) => e.event !== "tv_activation_requested").slice(0, 7);

  const firstName = (user?.display_name ?? "").split(" ")[0] || "there";

  return (
    <div className="ds-page">
      <PageHeader
        eyebrow="Maridive Reports"
        title={`${greeting()}, ${firstName}`}
        subtitle="Build a dashboard, check it as a draft, then publish it to the office screens."
        actions={
          <>
            <Button icon={FileInput} onClick={() => navigate("/admin/import/powerbi")}>Import from Power BI</Button>
            <Button variant="primary" icon={Plus} onClick={() => navigate("/admin/reports?new=1")}>New report</Button>
          </>
        }
      />

      <div className="ds-grid ds-grid--4" style={{ marginBottom: 20 }}>
        <Stat icon={BarChart3} label={`reports · ${published.length} published`} value={active.length} loading={reports.isLoading} onClick={() => navigate("/admin/reports")} />
        <Stat icon={Tv} tone="success" label={`TV screens active${pendingDevices.length ? ` · ${pendingDevices.length} waiting` : ""}`} value={activeDevices.length} loading={devices.isLoading} onClick={() => navigate("/admin/devices")} />
        <Stat icon={Database} tone="info" label="datasets connected" value={(datasets.data ?? []).filter((d) => d.is_active).length} loading={datasets.isLoading} onClick={() => navigate("/admin/data")} />
        <Stat
          icon={unhealthy.length ? AlertTriangle : CheckCircle2}
          tone={unhealthy.length ? "danger" : "success"}
          label="platform status"
          value={health.isLoading ? "…" : unhealthy.length ? "Attention" : "Healthy"}
          onClick={() => navigate("/admin/system-health")}
        />
      </div>

      {attention.length ? (
        <div className="ds-stack" style={{ marginBottom: 20, gap: 10 }}>
          {attention.map((a) => (
            <Banner key={a.key} tone={a.tone} title={a.title} action={<Button size="sm" iconRight={ArrowRight} onClick={() => navigate(a.to)}>{a.cta}</Button>}>
              {a.body}
            </Banner>
          ))}
        </div>
      ) : reports.isSuccess && devices.isSuccess ? (
        <div style={{ marginBottom: 20 }}>
          <Banner tone="success" title="Everything is up to date">No failed refreshes, no screens waiting, every service healthy.</Banner>
        </div>
      ) : null}

      <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)" }}>
        <Card title="Recent reports" subtitle="Pick up where you left off" actions={<Link to="/admin/reports">All reports</Link>} flush>
          {reports.isLoading ? (
            <SkeletonRows rows={5} />
          ) : recent.length === 0 ? (
            <EmptyState icon={BarChart3} title="No reports yet" description="Start from a template, import a Power BI report, or ask an AI assistant to build one." action={<Button variant="primary" icon={Plus} onClick={() => navigate("/admin/reports?new=1")}>New report</Button>} />
          ) : (
            <div className="ds-list">
              {recent.map((r) => {
                const st = reportStatus(r);
                return (
                  <div key={r.id} className="ds-list__row is-clickable" onClick={() => navigate(`/admin/reports/${r.id}`)}>
                    <span className="ds-stat__icon" style={{ width: 36, height: 36, borderRadius: 10 }}>
                      <BarChart3 size={17} />
                    </span>
                    <div className="ds-list__main">
                      <div className="ds-list__title ds-truncate">{r.name}</div>
                      <div className="ds-list__meta ds-truncate">
                        {r.page_count ? `${r.page_count} page${r.page_count > 1 ? "s" : ""} · ` : ""}
                        {r.owner_name ? `${r.owner_name} · ` : ""}edited {formatRelative(r.updated_at)}
                      </div>
                    </div>
                    <Badge tone={st.tone} dot>{st.label}</Badge>
                    <Button size="sm" variant="ghost" icon={PencilRuler} onClick={(e) => { e.stopPropagation(); navigate(`/admin/reports/${r.id}/edit`); }}>Edit</Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <div className="ds-stack">
          <Card title="Build faster" subtitle="Three ways to get a dashboard on screen">
            <div className="ds-stack" style={{ gap: 10 }}>
              <Button icon={Plus} onClick={() => navigate("/admin/reports?new=1")} style={{ justifyContent: "flex-start" }}>Start from a template</Button>
              <Button icon={FileInput} onClick={() => navigate("/admin/import/powerbi")} style={{ justifyContent: "flex-start" }}>Convert a Power BI report</Button>
              <Button icon={Bot} onClick={() => navigate("/settings/api-tokens")} style={{ justifyContent: "flex-start" }}>Let Claude or Codex build it</Button>
            </div>
          </Card>
          <Card title="Recent activity" actions={<Link to="/admin/audit">Audit log</Link>} flush>
            {audit.isLoading ? (
              <SkeletonRows rows={4} />
            ) : activity.length === 0 ? (
              <EmptyState icon={RefreshCw} title="No activity yet" />
            ) : (
              <div className="ds-list">
                {activity.map((e) => (
                  <div key={e.id} className="ds-list__row">
                    <div className="ds-list__main">
                      <div className="ds-small">
                        <strong>{(e.actor_id && nameOf.get(e.actor_id)) || (e.context?.display_name as string) || (e.actor_type === "system" ? "The platform" : e.actor_type === "user" ? "A user" : e.actor_type)}</strong>{" "}
                        {EVENT_LABELS[e.event] ?? e.event.replace(/_/g, " ")}
                      </div>
                      <div className="ds-list__meta">{formatRelative(e.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
